import {
  formatDate,
  getEngagementStatusVariant,
  getTextInCapitalize,
  getVariant,
} from "@/shared/utils/helperFunctions";
import ProspectProfileRow from "@/shared/components/ui/Avatar/ProspectProfileRow";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { EngagementScoreBreakdown } from "@/shared/components/ui/Status/EngagementScoreBreakdown";
import StepIndicator from "@/shared/components/ui/Status/StepStatus";
import { Trash2 } from "lucide-react";

export const createLeadsRenderCell = (openProfile, onDeleteClick) => (item, columnKey) => {
  switch (columnKey) {
    case "name":
      return (
        <div className="w-full text-wrap break-words">
          <ProspectProfileRow
          key={item.contact_id ?? item.id}
          id={item.contact_id ?? item.id}
          avatarUrl={item?.avatarUrl}
          name={item?.full_name ?? item?.name}
          email={item?.email}
          number={90}
          onClick={() => openProfile(item.contact_id ?? item.id)}
          className="cursor-pointer"
        />
        </div>
      );

    case "company":
      return <div className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words break-all">{item.company}</div>;

    case "emailVerification": {
      const status = String(item?.email_verification_status || "pending").toLowerCase();
      const isValid = status === "valid";
      const label = isValid ? "Valid" : status === "pending" ? "Pending" : "Not sendable";
      return (
        <StatusChip
          label={label}
          variant={isValid ? "success" : status === "pending" ? "warning" : "danger"}
          tooltipTitle={item?.email_verification_reason || "Email verification status"}
          tooltipContent={item?.email_verification_reason || "Email has not been verified yet."}
        />
      );
    }

    case "matchScore":
      return (
        <StatusChip
          label={`${item?.matchScore}%`}
          variant={getVariant(item?.matchScore)}
        />
      );

    case "current_step_order":
      return (
        <StatusChip
          label={`Step ${item?.current_step_order}`}
          variant={getVariant("neutral")}
        />
      );

    case "stage":
      return (
        <StepIndicator
          currentStep={
            item?.step === "Step 3" ? 3 : item?.step === "Step 2" ? 2 : item?.step === "Step 1" ? 1 : 0
          }
          hoverData={item?.step}
          variant="stage"
        />
      );

    case "engagementScore": {
      const temperature = item?.runtimeEngagementBreakdown?.temperature || "cold";
      return (
        <StatusChip
          label={`${getTextInCapitalize(temperature)} · ${item?.runtimeEngagementScore ?? 0}`}
          variant={getEngagementStatusVariant(temperature)}
          tooltipTitle="Engagement breakdown"
          tooltipPanelClassName="max-w-[min(352px,calc(100vw-32px))] px-3 py-3"
          tooltipContent={
            <EngagementScoreBreakdown
              score={item?.runtimeEngagementScore}
              breakdown={item?.runtimeEngagementBreakdown}
            />
          }
          tooltipCloseDelay={500}
        />
      );
    }

    case "created_at":
      return (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(item?.created_at || item?.updated_at)}
        </p>
      );

    case "actions":
      return (
        <div className="flex items-center justify-center gap-2">
          <Trash2
            size={16}
            className="text-gray-500 dark:text-gray-400 cursor-pointer hover:text-error-500 dark:hover:text-error-400"
            onClick={() => onDeleteClick(item.id)}
          />
        </div>
      );

    default:
      return <p className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words">{item[columnKey]}</p>;
  }
};
